/**
 * fias.io's side of a Company portal sign-in (ADR-024 §4.4.1, S10/D16).
 *
 *   /auth/company/:hostId?client_id…     the Continue step
 *   /auth/company/:hostId/sign-in        the branded sign-in (LoginPage)
 *   /auth/company/:hostId/register       the branded registration (open portals)
 *   /auth/company/:hostId/forgot         the branded password reset
 *
 * Continue is always an explicit click ("Continue to Devv as Jane"), never
 * silent — even for a user already signed in to fias.io. The server decides
 * everything (the request is validated, the portal servable, the user allowed,
 * the disclosure seen); this page shows what it decided. Every refusal is
 * neutral: it never says who belongs to what.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { BootSplash } from '@/components/BootSplash';
import { useAuth } from '@/auth/hooks/useAuth';
import { authApi, type CompanyAuthorizeParams } from '@/lib/auth-api';
import { queryKeys } from '@/lib/query-keys';
import { COMPANY_WEB_CALLBACK_PATH, COMPANY_WEB_HOST_ID_PATTERN } from './company-host-id';
import {
  COMPANY_REGISTERED_KEY,
  CompanyAuthBrandHeader,
  CompanyAuthBrandProvider,
  CompanyBrandTheme,
  SecuredByFias,
  toCompanyAuthBrand,
  useCompanyHostBrand,
  type CompanyAuthBrand,
} from './CompanyAuthBrand';
import { PORTAL_DISCLOSURE_VERSION, PortalDisclosure } from './PortalDisclosure';

const CONTINUE_KEY = (hostId: string) => `fias.company-continue.${hostId}`;

/** The OAuth request in the query, or null when anything is missing. */
export function parseAuthorizeParams(
  search: URLSearchParams,
): Omit<CompanyAuthorizeParams, 'hostId'> | null {
  const get = (k: string) => search.get(k) ?? '';
  const params = {
    clientId: get('client_id'),
    redirectUri: get('redirect_uri'),
    responseType: get('response_type'),
    state: get('state'),
    codeChallenge: get('code_challenge'),
    codeChallengeMethod: get('code_challenge_method'),
  };
  return Object.values(params).every((v) => v.length > 0) ? params : null;
}

/**
 * Where the browser goes with the code. Only ever the portal SERVER's
 * callback, which redeems it (the portal app never sees a code);
 * locally (`*.localhost` portals on the dev server) the same path on the dev
 * server's scheme and port.
 */
export function portalRedirectTarget(redirectUrl: string, portalHostname: string): string | null {
  let url: URL;
  try {
    url = new URL(redirectUrl);
  } catch {
    return null;
  }
  if (url.hostname !== portalHostname || url.pathname !== COMPANY_WEB_CALLBACK_PATH) return null;
  const here = window.location;
  if (portalHostname.endsWith('.localhost') && here.hostname === 'localhost') {
    return `${here.protocol}//${portalHostname}${here.port ? `:${here.port}` : ''}${url.pathname}${url.search}`;
  }
  return url.protocol === 'https:' ? url.toString() : null;
}

const REFUSALS: Record<string, string> = {
  HOST_ACCESS_DENIED: "You don't have access to this portal. Contact your administrator.",
  PRIVILEGED_ACCOUNT:
    'This account has Fias staff access, so it signs in on fias.io only — not through a Company portal.',
  HOST_UNAVAILABLE: "This portal isn't available right now.",
  RATE_LIMITED: 'Too many attempts. Please wait a minute and try again.',
};
const INVALID_LINK = "This sign-in link isn't valid. Go back to the portal and try again.";

function Frame({ brand, children }: { brand: CompanyAuthBrand | null; children: ReactNode }) {
  return (
    <CompanyBrandTheme primaryColor={brand?.primaryColor ?? null}>
      <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <div className="w-full max-w-sm">
            {brand && <CompanyAuthBrandHeader brand={brand} />}
            <div className="bg-card rounded-xl border shadow-sm p-6 space-y-4">{children}</div>
            <SecuredByFias />
          </div>
        </div>
      </div>
    </CompanyBrandTheme>
  );
}

function Refusal({
  brand,
  message,
  onSwitchAccount,
  switchLabel = 'Sign in with a different account',
}: {
  brand: CompanyAuthBrand | null;
  message: string;
  /** Offered when the refusal is about WHO is signed in, not about the link or the portal. */
  onSwitchAccount?: () => void;
  switchLabel?: string;
}) {
  return (
    <Frame brand={brand}>
      <p role="alert" className="text-sm">
        {message}
      </p>
      {onSwitchAccount && (
        <Button variant="outline" className="w-full" onClick={onSwitchAccount}>
          {switchLabel}
        </Button>
      )}
    </Frame>
  );
}

/** Refusals about the signed-in account — another account may be allowed. */
const ACCOUNT_REFUSALS = new Set(['PRIVILEGED_ACCOUNT', 'HOST_ACCESS_DENIED']);
/** The fias.io session has no refresh token to hand on — signing in again fixes it. */
const SESSION_REFUSALS = new Set(['SESSION_EXPIRED', 'NO_REFRESH_TOKEN']);

function readRegisteredHere(hostId: string): boolean {
  try {
    return window.sessionStorage.getItem(COMPANY_REGISTERED_KEY(hostId)) === '1';
  } catch {
    return false;
  }
}

function rememberContinue(hostId: string, path: string) {
  try {
    window.sessionStorage.setItem(CONTINUE_KEY(hostId), path);
  } catch {
    // The route state carries it too; storage only survives a refresh.
  }
}

function recallContinue(hostId: string): string | null {
  try {
    return window.sessionStorage.getItem(CONTINUE_KEY(hostId));
  } catch {
    return null;
  }
}

/** `/auth/company/:hostId` — the Continue step. */
export function CompanyAuthorizePage() {
  const { hostId = '' } = useParams();
  const [search] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user, signOut } = useAuth();
  const validHost = COMPANY_WEB_HOST_ID_PATTERN.test(hostId);
  const oauth = parseAuthorizeParams(search);
  // The branded portal (the URL) travels with the request: the API refuses
  // unless it is the portal the client belongs to.
  const params: CompanyAuthorizeParams | null = oauth && validHost ? { ...oauth, hostId } : null;
  const brandQuery = useCompanyHostBrand(validHost ? hostId : undefined);
  const continuePath = `${location.pathname}${location.search}`;
  const brand = brandQuery.data ? toCompanyAuthBrand(brandQuery.data, continuePath) : null;

  const view = useQuery({
    queryKey: queryKeys.companyPortalSignIn.authorize(
      hostId,
      user?.userId ?? '',
      search.toString(),
    ),
    queryFn: () => authApi.getCompanyAuthorize(params!),
    enabled: !!params && isAuthenticated,
    retry: false,
    staleTime: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (params) rememberContinue(hostId, continuePath);
  }, [params, hostId, continuePath]);

  // Sign out of fias.io and come back to this same request as someone else —
  // the branded sign-in, never fias.io's own home.
  const switchAccount = async () => {
    await signOut();
    if (brand) navigate(brand.paths.signIn, { state: { from: continuePath }, replace: true });
  };

  if (!params) return <Refusal brand={null} message={INVALID_LINK} />;
  if (authLoading || brandQuery.isLoading) return <BootSplash />;
  if (!brand) return <Refusal brand={null} message={REFUSALS.HOST_UNAVAILABLE} />;
  if (!isAuthenticated) {
    return <Navigate to={brand.paths.signIn} state={{ from: continuePath }} replace />;
  }
  if (view.isLoading) return <BootSplash />;
  if (view.isError) {
    const code = (view.error as { code?: string })?.code ?? '';
    if (SESSION_REFUSALS.has(code)) {
      // The fias.io session can't hand a portal a session: signing in again
      // fixes it, and "go back to the portal" would only loop back here.
      return (
        <Refusal
          brand={brand}
          message="Your Fias session has expired. Please sign in again."
          onSwitchAccount={() => void switchAccount()}
          switchLabel="Sign in again"
        />
      );
    }
    return (
      <Refusal
        brand={brand}
        message={REFUSALS[code] ?? INVALID_LINK}
        onSwitchAccount={ACCOUNT_REFUSALS.has(code) ? () => void switchAccount() : undefined}
      />
    );
  }
  const data = view.data!;
  // Belt to the API's braces: never show one Company's brand around a
  // Continue that sends the code to another's portal.
  if (data.portal.hostId !== hostId) return <Refusal brand={null} message={INVALID_LINK} />;
  // The headline is the Company; the portal's own name, when it adds
  // anything, and its address sit beneath.
  const companyLabel = data.portal.brand?.displayName || data.portal.companyName || brand.name;
  const portalLabel =
    data.portal.displayLabel && data.portal.displayLabel !== companyLabel
      ? data.portal.displayLabel
      : null;
  const who = user?.displayName || (user?.username ? `@${user.username}` : 'you');

  const onContinue = async () => {
    setSubmitting(true);
    setError('');
    try {
      const registeredHere = readRegisteredHere(hostId);
      const { redirectUrl } = await authApi.continueToCompany({
        ...params,
        // The version this page SHOWED, not the server's: a consent is to the
        // words on screen.
        ...(data.needsDisclosure ? { disclosureVersion: PORTAL_DISCLOSURE_VERSION } : {}),
        ...(registeredHere ? { registeredHere: true } : {}),
      });
      const target = portalRedirectTarget(redirectUrl, data.portal.hostname);
      if (!target) throw new Error('unexpected redirect');
      try {
        window.sessionStorage.removeItem(COMPANY_REGISTERED_KEY(hostId));
        window.sessionStorage.removeItem(CONTINUE_KEY(hostId));
      } catch {
        // Nothing to clean up.
      }
      window.location.assign(target);
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      setError(
        REFUSALS[code] ??
          (code === 'SESSION_EXPIRED' || code === 'NO_REFRESH_TOKEN'
            ? 'Your Fias session has expired. Please sign in again.'
            : 'Something went wrong. Please try again.'),
      );
      setSubmitting(false);
    }
  };

  return (
    <Frame brand={brand}>
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">Continue to {companyLabel}</h1>
        <p className="text-sm text-muted-foreground break-all">
          {portalLabel ? `${portalLabel} · ` : ''}
          {data.portal.hostname}
        </p>
      </div>
      {data.needsDisclosure && (
        <p className="rounded-md border p-3 text-sm">
          <PortalDisclosure company={companyLabel} />
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button className="w-full h-11" disabled={submitting} onClick={() => void onContinue()}>
        {submitting ? 'Continuing…' : `Continue as ${who}`}
      </Button>
      <button
        type="button"
        className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
        onClick={() => void switchAccount()}
      >
        Not you? Sign in with another account
      </button>
    </Frame>
  );
}

/**
 * Wraps a platform auth page (sign-in, register, forgot) in the portal's
 * brand. A members-only portal has no registration: that route goes to
 * sign-in.
 */
export function CompanyBrandedAuthRoute({
  children,
  registration = false,
}: {
  children: ReactNode;
  registration?: boolean;
}) {
  const { hostId = '' } = useParams();
  const location = useLocation();
  const validHost = COMPANY_WEB_HOST_ID_PATTERN.test(hostId);
  const brandQuery = useCompanyHostBrand(validHost ? hostId : undefined);
  if (!validHost) return <Refusal brand={null} message={INVALID_LINK} />;
  if (brandQuery.isLoading) return <BootSplash />;
  if (!brandQuery.data) return <Refusal brand={null} message={REFUSALS.HOST_UNAVAILABLE} />;
  const continueTo =
    (location.state as { from?: string } | null)?.from ??
    recallContinue(hostId) ??
    `/auth/company/${hostId}`;
  const brand = toCompanyAuthBrand(brandQuery.data, continueTo);
  if (registration && !brand.allowRegistration) {
    return <Navigate to={brand.paths.signIn} state={location.state} replace />;
  }
  return <CompanyAuthBrandProvider brand={brand}>{children}</CompanyAuthBrandProvider>;
}
